/* ===================================================================
   THE STAGE — the purchase section's left column, as a product being built.

   `snippets/bottle-stage.liquid` renders the chosen pack's bottles from the
   server. This file is everything that happens after: the typed name appearing on
   the lead bottle, the chosen style tinting the art window, the generated label
   landing on every bottle in the pack, and all of it coming back after the section
   morphs.

   **Why it is its own file rather than more of `generator.js`.** Three reasons,
   and the third is the one that matters. It is a different column, so a defect
   here cannot take the configurator down with it. It is a different lifetime — the
   stage has to re-hydrate on a morph that leaves `generator.js`'s own resync
   early-returning, because that function is written to skip when no label exists
   and the *name* has to come back whether a label exists or not. And it reads
   state rather than owning it: `generator.js` remains the only writer of
   `ai-pet-generator:<productId>`, and this file is a second reader of it, which is
   a relationship that cannot drift the way two writers can.

   **What the morph actually does, measured, because `D-50` says something else.**
   That row recorded that choosing a package "morphs the whole
   `featured-product-information` element from fresh server HTML". Today
   `assets/variant-picker.js`'s `updateElement` morphs exactly three subtrees —
   `variant-picker`, `product-form-component` and `product-price` — with the
   whole-element morph commented out beneath them. Measured on the rendered page:
   after choosing the six-bottle package the recap read "6-Bottle Soul Dog
   Selection" and this column was still showing three bottles.

   Two consequences shape this file. The stage cannot wait for a re-render that
   never arrives, so every package's bottles are server-rendered once and
   `syncPack` moves one attribute between them. And the configurator's own inputs
   are *outside* those three subtrees, so a visitor's photo file and typed name
   survive a package change untouched — which is the thing `HQ-70` asked someone to
   check and is now checked.

   Progressive enhancement: with this file absent or broken, the stage renders its
   still composition — the chosen pack's bottles wearing an empty cream band — and
   the flow is unaffected. Nothing here is required to complete a purchase.
   =================================================================== */

(() => {
  if (window.__sdwStageBooted) return;
  window.__sdwStageBooted = true;

  const ROOT = document.documentElement;
  const reduced = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  /* ------------------------------------------------------------------ */
  /* the analytics surface                                              */
  /* ------------------------------------------------------------------ */
  /*
    The copy deck's guardrail names four events to instrument before the reorder
    ships — configurator start, generation complete, varietal selected and add to
    cart — so the funnel before and after can be compared honestly. The varietal
    option is dead in admin (`HQ-56`), so **package selected** takes its place and
    the substitution is recorded in `docs/DECISIONS.md`.

    Each fires twice, into two places that cost nothing when nobody is listening: a
    `CustomEvent` on `document`, which a Shopify web pixel or any tag manager can
    subscribe to without touching this theme, and a `dataLayer` push for GA4. There
    is no analytics library in this theme to call into — `docs/HUMAN_QUEUE.md`
    carries the wiring as the human step, because connecting these to GA4 is an
    admin action rather than a theme one.

    The triggers are defined rather than assumed, because a pre/post comparison is
    only honest if both sides mean the same thing:

    - `configurator_start` — the first *explicit* interaction with a step control or
      with the stage. Not page load, not a scroll past it, and it fires once.
    - `package_selected` — an explicit pack choice, **including** confirming the
      first-listed default by advancing past step 1, which is what `D-38` makes the
      common path. Without that second trigger the event would under-count every
      visitor who accepted the default, which is most of them.
    - `generation_complete` — a label came back.
    - `add_to_cart` — the press survived the requirements guard.
  */
  let started = false;
  const track = (name, detail = {}) => {
    try {
      document.dispatchEvent(new CustomEvent(`sdw:${name}`, { detail, bubbles: true }));
    } catch (_) {}
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: `sdw_${name}`, ...detail });
    } catch (_) {}
  };
  const trackStart = (source) => {
    if (started) return;
    started = true;
    track('configurator_start', { source });
  };
  window.SDW_TRACK = track;
  window.SDW_TRACK_START = trackStart;

  /* An explicit pack choice. The *other* way this event fires is from
     `assets/buy-flow-steps.js`, when the visitor advances past step 1 without
     touching a card — which confirms the first-listed default `D-38` preselects,
     and is the common path rather than an edge case. Counting only the taps would
     under-report package selection by most of the traffic. Both routes guard on
     the same flag, so a visitor who taps a card and then advances is counted once. */
  let packTracked = false;
  const trackPackage = (value, how) => {
    if (packTracked) return;
    packTracked = true;
    track('package_selected', { package: value || '', via: how });
  };
  window.SDW_TRACK_PACKAGE = trackPackage;

  document.addEventListener('change', (event) => {
    const input = event.target instanceof Element ? event.target.closest('.sdw-pack__input') : null;
    if (!input) return;
    trackStart('package');
    trackPackage(input.getAttribute('value'), 'card');
    syncPack();
  });

  /* **A stage is only the start of a configurator session where there is a
     configurator.** `sections/label-cta.liquid` puts a stage on the home page as a
     picture of the product with a link under it, and a visitor who taps the bottles
     there has started nothing — they have not reached a control. Counting that tap as
     `configurator_start` would inflate the denominator of the one funnel the copy deck
     names as this reorder's guardrail, and it would do it with the traffic least likely
     to convert: everybody who scrolled past a band and poked the picture.

     Tested rather than assumed from the template, and tested against the same element
     `assets/buy-flow-steps.js` tests for when it decides whether the flow exists at all,
     so the two files cannot disagree about what "there is a configurator here" means. */
  const hasConfigurator = () => !!document.querySelector('[data-section-id="ai-pet-generator-block"]');

  /* The first explicit interaction, whatever it is. `pointerdown` and `keydown`
     rather than `click`, so a visitor who tabs into the flow and types counts as
     having started it; `capture` so a control that stops propagation cannot hide
     the start of the session it belongs to. */
  for (const type of ['pointerdown', 'keydown']) {
    document.addEventListener(
      type,
      (event) => {
        if (started) return;
        const el = event.target instanceof Element ? event.target : null;
        if (!el) return;
        if (el.closest('.product-details [data-sdw-step], [data-sdw-stage], [data-sdw-stage-toggle]')) {
          if (!hasConfigurator()) return;
          trackStart(el.closest('[data-sdw-stage], [data-sdw-stage-toggle]') ? 'stage' : 'step');
        }
      },
      { capture: true }
    );
  }

  /* ------------------------------------------------------------------ */
  /* state                                                              */
  /* ------------------------------------------------------------------ */
  const stageRoot = () => document.querySelector('[data-sdw-stage]');
  const bottles = () => Array.from(document.querySelectorAll('[data-sdw-bottle]'));

  /* **Two places to look, because the stage now outlives the configurator.**
     `#ai-pet-generator-an` is rendered by `snippets/ai-generator.liquid` and is the
     answer wherever a configurator is on the page. `sections/label-cta.liquid` puts a
     stage on the home page with no configurator beside it, and there this returned the
     literal `unknown` and read `ai-pet-generator:unknown` — an empty bucket, so a
     visitor who had already made labels on the product page was shown the placeholder
     on their own bottles.

     The fallback is the same attribute on the stage's own root, rendered by
     `snippets/bottle-stage.liquid` on every page that draws a stage. One more place to
     look, not a second mechanism: same attribute name, same value, and the anchor still
     wins where both exist so nothing about the configurator page changes. */
  const productId = () => {
    const anchor = document.getElementById('ai-pet-generator-an');
    const fromAnchor = String(anchor?.getAttribute?.('product-id') || '').trim();
    if (fromAnchor) return fromAnchor;
    const root = document.querySelector('[data-sdw-stage-root]');
    return String(root?.getAttribute?.('product-id') || '').trim() || 'unknown';
  };

  const readStored = () => {
    try {
      const raw = localStorage.getItem(`ai-pet-generator:${productId()}`);
      if (!raw) return null;
      const data = JSON.parse(raw);
      return data && typeof data === 'object' ? data : null;
    } catch (_) {
      return null;
    }
  };

  /* The last thing applied, so a morph can be answered without re-reading storage
     on every mutation and so `setLabel` can tell a genuine change from a repaint —
     which is what keeps the settle from playing twice. */
  /* `neutral` since `D-113`. There is no style control any more, so before a label
     lands there is no style — the art window's ground says "waiting for its
     picture" rather than standing in for a decision nobody has made. After a label
     is chosen the ground follows *that label's* style. */
  const applied = { name: '', style: 'neutral', label: '' };

  /* ------------------------------------------------------------------ */
  /* the package                                                        */
  /* ------------------------------------------------------------------ */
  /*
    **The stage does not wait for the section to re-render, because it never
    would.** `D-50` recorded that choosing a package "morphs the whole
    `featured-product-information` element from fresh server HTML"; today
    `assets/variant-picker.js`'s `updateElement` morphs three subtrees —
    `variant-picker`, `product-form-component` and `product-price` — and leaves the
    media column alone. Measured: the recap said "6-Bottle Soul Dog Selection"
    while this column was still showing three bottles.

    So every package's bottles are server-rendered once and this moves one
    attribute between them. Nothing here parses the package map — that stays in
    Liquid, read by the same snippet the package cards read it with — and the
    switch lands in the same frame as the tap rather than after a round trip.

    **The bar's package name and price are copied from the chosen card, not
    re-derived.** The card is inside `variant-picker`, which *is* morphed, so it
    always carries the server's own answer; reading its rendered strings is what
    makes the collapsed stage structurally incapable of disagreeing with the card
    above it, which is `D-15`'s rule applied one surface further along.
  */
  const checkedPackInput = () =>
    document.querySelector('fieldset[data-sdw-step="1"] input[type="radio"]:checked') ||
    document.querySelector('.sdw-pack__input:checked');

  const syncPack = () => {
    const stage = stageRoot();
    const input = checkedPackInput();
    if (!stage || !input) return;

    const value = String(input.getAttribute('value') || '').trim();
    if (!value) return;

    const groups = Array.from(document.querySelectorAll('[data-sdw-stage-pack]'));
    const next = groups.find((group) => group.getAttribute('data-sdw-pack-value') === value);

    if (next && !next.hasAttribute('data-sdw-current')) {
      for (const group of groups) group.removeAttribute('data-sdw-current');
      next.setAttribute('data-sdw-current', '');
    }
    if (next) {
      const size = next.getAttribute('data-sdw-pack-size') || '3';
      if (stage.getAttribute('data-sdw-pack-size') !== size) stage.setAttribute('data-sdw-pack-size', size);
    }
    if (stage.getAttribute('data-sdw-pack') !== value) stage.setAttribute('data-sdw-pack', value);

    /* The collapsed stage in the flow bar, from the card the visitor chose. */
    const card = input.closest('.sdw-pack');
    const packName = card?.querySelector('.sdw-pack__name')?.textContent?.trim() || value;
    /* **The price is read off the twin, not off the wrapper.** `price-cents` renders the
       figure twice inside `.sdw-pack__amount` — once for the eye with the cents as a
       superior figure and no decimal point, once as a `.visually-hidden` twin carrying
       the money filter's own string, because a `<sup>` between the dollars and the cents
       reads as two numbers in a row. `textContent` on the wrapper concatenates both, so
       this line used to hand the bar `$9999$99.99`.

       The twin is also the spelling `snippets/buy-flow-steps.liquid:115` server-renders
       into this bar, so the sync now preserves what it found rather than replacing it
       with a second convention. Falls back to the wrapper for a currency `price-cents`
       passes through unsplit — a comma decimal or no minor unit — where there is no twin
       and the wrapper's own text is already the plain figure. */
    const packAmount = card?.querySelector('.sdw-pack__amount');
    const packPrice = (packAmount?.querySelector('.visually-hidden') || packAmount)?.textContent?.trim() || '';

    for (const el of document.querySelectorAll('.sdw-flow-nav__pack')) {
      if (el.textContent !== packName) el.textContent = packName;
    }
    for (const el of document.querySelectorAll('.sdw-flow-nav__price')) {
      if (packPrice && el.textContent !== packPrice) el.textContent = packPrice;
    }

    /* The bar shows the lead bottle of the package now chosen. Read off the group
       that is current rather than re-deriving the glass, for the same reason as
       the name and the price. */
    const lead = next?.querySelector('.sdw-bottle__img');
    const barImg = document.querySelector('.sdw-flow-nav__bottle-img');
    if (lead && barImg) {
      const src = lead.getAttribute('src');
      if (src && barImg.getAttribute('src') !== src) barImg.setAttribute('src', src);
    }

    announce();
    /* **The composite is per package, not only per label.** A visitor who draws a
       label on the three-bottle pack and then switches to six must not carry a
       picture of three bottles into the cart. Debounced inside `buildComposite`, so
       a visitor flicking between cards pays for one composite rather than four. */
    buildComposite();
  };

  /* ------------------------------------------------------------------ */
  /* the summary — the stage's whole screen-reader surface              */
  /* ------------------------------------------------------------------ */
  /*
    The bottle group is `aria-hidden`: it is a picture of the thing the controls
    already describe, and announcing six bottles' worth of decoration on every
    keystroke would be worse than silence. This one line is what replaces it, and it
    is polite, so it waits for a pause rather than interrupting the field being
    typed into.
  */
  const announce = () => {
    const el = document.querySelector('[data-sdw-stage-summary]');
    const stage = stageRoot();
    if (!el || !stage) return;
    const pack = stage.getAttribute('data-sdw-pack') || 'your package';
    const name = applied.name.trim();
    const text = applied.label
      ? name
        ? `Your ${pack} with ${name}’s label.`
        : `Your ${pack} with your label.`
      : name
        ? `Your ${pack}, ${name}’s name on the bottle.`
        : `Your ${pack}.`;
    if (el.textContent !== text) el.textContent = text;
  };

  /* ------------------------------------------------------------------ */
  /* applying state to the bottles                                      */
  /* ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------ */
  /* the recap — the same state, read a second way                      */
  /* ------------------------------------------------------------------ */
  /*
    The review step's name and label rows are two views of exactly what is on the
    bottle, so they are painted from here rather than from a second reader of the
    same storage. A recap that can disagree with the picture beside it is worse
    than no recap, and two files reading one key is how that happens.

    Both rows are server-rendered with their empty state already in them and the
    filled state `hidden`; this swaps which of the two is shown. Nothing is
    inserted or removed, so a row cannot change height when it fills and the
    button below it cannot move under a thumb.
  */
  const paintRecap = () => {
    const name = applied.name.trim();

    for (const el of document.querySelectorAll('[data-sdw-recap-name]')) {
      el.textContent = name;
      el.hidden = !name;
      const missing = el.parentElement?.querySelector('[data-sdw-recap-goto="name"]');
      if (missing) missing.hidden = !!name;
    }

    /* Which sentence the empty label row shows depends on *why* it is empty, and
       the two are different asks: nothing drawn yet, or drawn but not chosen. The
       add-to-cart guard makes the same distinction in the same words. */
    const stored = readStored();
    const drawn = Array.isArray(stored?.generatedImages) ? stored.generatedImages.filter((i) => i && i.url) : [];
    const chosen = applied.label;

    for (const el of document.querySelectorAll('[data-sdw-recap-label]')) {
      el.hidden = !chosen;
      const missing = el.parentElement?.querySelector('[data-sdw-recap-goto="label"]');
      if (missing) {
        missing.hidden = !!chosen;
        /* Plural since `D-113`, and "make" since `D-114`, when the client changed
           the button's verb. The configurator's add-to-cart requirement carries the
           identical string — one voice, two places — so changing it here without
           changing it there is the defect this comment exists to prevent. */
        const text = drawn.length ? 'Choose one of your labels.' : 'Make your labels.';
        if (missing.textContent.trim() !== text) missing.textContent = text;
      }
    }

    for (const thumb of document.querySelectorAll('[data-sdw-recap-thumb]')) {
      if (chosen) {
        thumb.style.backgroundImage = `url("${chosen.replace(/"/g, '%22')}")`;
        thumb.setAttribute('data-sdw-filled', '');
      } else {
        thumb.style.removeProperty('background-image');
        thumb.removeAttribute('data-sdw-filled');
      }
    }
  };

  /* The recap's empty rows link back to the control that fills them. On a phone
     that control is on a screen the visitor is not on, and neither a scroll nor a
     focus does anything to a `display: none` element — so this dispatches the same
     `sdw:reveal` the add-to-cart guard uses, which `assets/buy-flow-steps.js`
     answers by bringing that step forward. Above the breakpoint nothing listens
     and the plain scroll is the whole behaviour. */
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-sdw-recap-goto]') : null;
    if (!link) return;
    const selector = link.getAttribute('href') || '';
    const target = selector.startsWith('#') ? document.querySelector(selector) : null;
    if (!target) return;
    event.preventDefault();
    const step = target.closest('[data-sdw-step]');
    try { step?.dispatchEvent(new CustomEvent('sdw:reveal', { bubbles: true })); } catch (_) {}
    try {
      (step || target).scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
    } catch (_) {}
    try { target.focus({ preventScroll: true }); } catch (_) {}
  });

  const setName = (value) => {
    const next = String(value == null ? '' : value);
    applied.name = next;
    for (const el of document.querySelectorAll('[data-sdw-name]')) {
      if (el.textContent !== next) el.textContent = next;
    }
    const stage = stageRoot();
    if (stage) stage.toggleAttribute('data-sdw-has-name', !!next.trim());
    paintRecap();
    announce();
  };

  const setStyle = (value) => {
    /* The signature is unchanged — `generator.js` still calls this on hydrate and
       on every label choice — and the default moved from a style to the neutral
       ground. A falsy value means "nothing chosen", which is a real state now
       rather than an impossible one. */
    const next = String(value || 'neutral');
    applied.style = next;
    for (const el of document.querySelectorAll('[data-sdw-art]')) {
      if (el.getAttribute('data-sdw-ground') !== next) el.setAttribute('data-sdw-ground', next);
    }
  };

  /*
    The settle plays **once, at the reveal**, and never on a re-hydration, a reload
    or a pack change. That distinction is the whole reason `settle` is an argument
    rather than a consequence of the label changing: incoming bottles on a pack
    switch arrive already wearing the current label, because a group of bottles that
    each animate their label into place every time the visitor compares two packages
    reads as a page that is loading rather than as a product that is made.
  */
  /*
    **The band takes the picture's shape, because the picture is the label.**

    In the whole-label branch the generated image is not decoration inside a frame
    the theme drew — it *is* the frame, and a box that keeps the theme's own
    proportions would either crop the visitor's label or letterbox it. Neither is
    something to ship on the one surface whose job is showing them what they made.

    So the image is measured once, off-screen, and its true aspect is published as
    `--sdw-label-aspect` for the CSS to shape every band with — the bottles' and
    the phone bar's alike. Until it resolves the band keeps the mock-up's own 0.59,
    which is what the label the generator is drawing to should come back as anyway;
    the measurement is the guard, not the mechanism.

    Two things it deliberately does not do. It does not block the label appearing —
    the background image is already set by the time this runs, so a slow decode
    costs a reshape, not a blank bottle. And it does not apply a result whose URL is
    no longer the current one: a visitor who draws twice quickly would otherwise get
    the first label's shape under the second label's picture.

    The clamp is loose on purpose now, and that is a consequence of where the
    geometry moved to rather than a relaxation of care. The label's box no longer
    *is* this ratio — its width is a fixed share of the glass and its height is a
    `clamp()` in the stylesheet — so a strange number can only push the height to
    one end of a range that is already bounded. All this has to exclude is a value
    that would make the calculation meaningless: a decoded size of zero, or an
    image so far from a label's proportions that it is not one.
  */
  /* `src` is what to decode — the blob URL where there is one, because decoding a
     115KB data URI a second time is work with no answer of its own. `token` is the
     label the measurement belongs to, which is the *original* url, since that is
     what `applied.label` holds and what a later call would compare against. */
  const measureLabelAspect = (src, token) => {
    const root = document.documentElement;
    let img = null;
    try {
      img = new Image();
    } catch (_) {
      return;
    }
    img.decoding = 'async';
    img.addEventListener('load', () => {
      if (applied.label !== token) return;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) return;
      const aspect = Math.min(1.6, Math.max(0.3, w / h));
      root.style.setProperty('--sdw-label-aspect', String(Math.round(aspect * 1000) / 1000));
    });
    img.src = src;
  };

  const clearLabelAspect = () => {
    document.documentElement.style.removeProperty('--sdw-label-aspect');
  };

  /* ------------------------------------------------------------------ */
  /* one decode per source, shared by the two things that draw           */
  /* ------------------------------------------------------------------ */
  /*
    Two consumers below want the same pictures as bitmaps — the warp bake wants the
    label, the pack composite wants the label and the three bottle renders, and six
    bottles share those three. So the cache sits above both rather than inside
    either.

    **Fetched rather than drawn from the live `<img>`:** a shop file served
    cross-origin would taint the canvas and `toBlob` would throw at the end of all
    that work, whereas a CORS fetch either yields a clean bitmap or fails early and
    cheaply.
  */
  const bitmapCache = new Map();
  const bitmapFor = async (src) => {
    if (!src) return null;
    if (bitmapCache.has(src)) return bitmapCache.get(src);
    const promise = (async () => {
      const res = await fetch(src, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) throw new Error('fetch failed');
      return await createImageBitmap(await res.blob());
    })();
    bitmapCache.set(src, promise);
    try {
      return await promise;
    } catch (_) {
      bitmapCache.delete(src);
      return null;
    }
  };

  /* **The bottle renders are cached forever and the labels must not be.** The three
     renders are stable asset URLs shared by six bottles, which is what the cache is
     for. A label is a fresh blob URL every redraw, so keeping its entry pins a
     decoded 1024×1280 bitmap — about 5MB — for a URL that has already been revoked,
     once per draw for the life of the session. Dropping the entry is enough; the
     bitmap is not closed, because a bake or a composite that is mid-draw may still
     be holding it and closing it under them would throw. */
  const dropBitmap = (src) => {
    if (src) bitmapCache.delete(src);
  };

  /* ------------------------------------------------------------------ */
  /* the picture, made short before it is published                     */
  /* ------------------------------------------------------------------ */
  /*
    **`/apps/gemini/generate` returns the label as a base64 `data:` URL, and one of
    those must never reach an inherited custom property.** Read off a real
    generation on the live store on 2026-08-21: `selectedImage` was
    `data:image/webp;base64,…`, 115,743 characters. `--sdw-label-img` is published
    on `documentElement` and inherited by the whole stage subtree, so publishing the
    string itself puts a copy of it in every descendant's computed style and every
    invalidation of that subtree re-resolves all of them. When this was measured the
    subtree carried a ladder of warp slices — 240 across the three packages, 112
    painting at once on the six-bottle group — which is what made it enormous. The
    ladder has since been baked into one bitmap and the multiplier with it, **but the
    rule stands**: the property is still inherited, and a long string in an inherited
    custom property is a cost that scales with whatever is under it.

    Measured on the rendered page with 112 painted slices and a 121KB data URL
    (`.impeccable/shape/2026-08-20-label-warp-evaluation.md`,
    `.impeccable/shape/2026-08-20-warp-and-composite-plan.md` §0.2): the worst single
    style recalculation went from 5.8ms to 40.8ms, and the synchronous flush inside
    `playSettle` from 4.4ms to 131.4ms. On a four-times-slower CPU — a mid-range
    phone — 18.5ms became **508ms**, a main thread blocked for half a second at the
    exact moment the label lands. Scrolling is unaffected either way; it is
    invalidation that costs, which is why the label reveal and the package switch are
    where it showed.

    So the data URI is converted to a blob URL once and the short one is what gets
    published. **Synchronously**, by design: `setLabel` is called from the
    re-hydration path and from `clearStage`, and making it async would put a frame
    between the label arriving and the bottles wearing it — a flash where there is
    none today.

    **The original is untouched.** `applied.label` still holds it, `generator.js`
    still owns it, and the results grid, the PDF and the `_AI Image` cart property
    all still carry the flat original. This converts what is *displayed*, nothing
    else.

    If the app is ever changed to return a hosted URL, this becomes a pass-through
    and costs a string comparison. It should stay either way.
  */
  let ownedBlobUrl = '';
  let ownedBlobSource = '';

  /* The short URL currently published to the CSS — the blob where one was made, the
     original where none was needed. `setLabel` reads it back on the next call as the
     picture to fade *from*, which is why it is remembered here rather than recomputed
     from `applied.label`: by then that already holds the incoming label. */
  let shownUrl = '';

  /*
    **Revoked a beat late, and that is what the cross-fade is standing on.** Choosing
    a second label publishes a new picture and used to revoke the old one in the same
    statement — correct while the swap was instantaneous, and fatal to a fade, whose
    whole job is to keep painting the outgoing label for 300ms after it stopped being
    the current one. A revoked blob URL paints nothing, so the fade would have been a
    label vanishing rather than one label becoming another.

    A timer is the entire bookkeeping. Nothing else holds these URLs: the bitmap
    cache entry is dropped immediately, which is the part that actually pins memory
    (a decoded 1024×1280 label is about 5MB against the blob's 115KB), and what is
    kept for half a second is the small half. `RETIRE_MS` is the fade plus a margin;
    it must stay longer than `sdw-label-crossfade` in `assets/generator.css`.
  */
  const RETIRE_MS = 600;

  const retireUrl = (url) => {
    if (!url) return;
    setTimeout(() => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {}
    }, RETIRE_MS);
  };

  const releaseOwnedBlob = () => {
    if (!ownedBlobUrl) return;
    retireUrl(ownedBlobUrl);
    dropBitmap(ownedBlobUrl);
    ownedBlobUrl = '';
    ownedBlobSource = '';
  };

  const dataUrlToBlob = (dataUrl) => {
    const comma = dataUrl.indexOf(',');
    if (comma < 0) throw new Error('not a data url');
    const header = dataUrl.slice(0, comma);
    const body = dataUrl.slice(comma + 1);
    const mime = (header.match(/^data:([^;,]+)/) || [])[1] || 'image/png';
    const binary = header.includes(';base64') ? atob(body) : decodeURIComponent(body);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  };

  /* Never fails to produce something showable: a conversion that throws returns the
     original, because a slow label is better than no label. */
  const publishableUrl = (url) => {
    if (!url || !url.startsWith('data:')) {
      releaseOwnedBlob();
      return url || '';
    }
    if (url === ownedBlobSource && ownedBlobUrl) return ownedBlobUrl;
    releaseOwnedBlob();
    try {
      ownedBlobUrl = URL.createObjectURL(dataUrlToBlob(url));
      ownedBlobSource = url;
      return ownedBlobUrl;
    } catch (_) {
      return url;
    }
  };

  /* ------------------------------------------------------------------ */
  /* the warp, baked once instead of painted band by band               */
  /* ------------------------------------------------------------------ */
  /*
    **The warp itself is in `assets/label-warp.js` now, and so is the account of what
    it is and what it buys** (`D-188`). Read it there. What stays here is everything
    that is about *this* stage rather than about the curve: which picture gets baked,
    when a bake is cancelled, how the blob is retired, and where the result is
    published.

    **A second property rather than an overwrite.** `--sdw-label-img` keeps carrying
    the flat picture, because the phone bar's collapsed band is a thumbnail and wants
    the unwarped one, and because the results grid, the PDF and `_AI Image` are all
    still the flat label's. The cascade in `assets/generator.css` reads
    `var(--sdw-label-warped-img, var(--sdw-label-img, none))`, so between the label
    landing and the bake resolving — single-digit milliseconds — the flat picture
    shows inside the bowed paper, which is what the pre-warp build shipped. The
    failure mode is a correctly outlined flat label, never a half-warped one.
  */
  const WARP_PROP = '--sdw-label-warped-img';

  let warpUrl = '';
  let warpSource = '';
  let warpPending = '';
  let warpToken = 0;

  /* Retired rather than revoked, for the reason `retireUrl` gives above: this is the
     picture the cross-fade copies, because the baked cylinder is what was on the
     bottle rather than the flat original it was baked from. */
  const revokeWarp = () => {
    if (!warpUrl) return;
    retireUrl(warpUrl);
    warpUrl = '';
    warpSource = '';
  };

  /* Leak one of these per redraw and a long session accumulates megabytes, so the
     bitmap is revoked on replacement and on the stage's own reset. Bumping the token
     is what stops a bake already in flight from publishing over a cleared stage. */
  const clearWarp = () => {
    warpToken += 1;
    revokeWarp();
    document.documentElement.style.removeProperty(WARP_PROP);
  };

  /* **The curve moved out of this file and these two call it** (`D-188`). It stood
     here, and stood here alone, for as long as the configurator was the only surface
     wearing a label. The home page hero wears one now, and a second copy of a
     parabola is a parabola that can be half-changed — which is the same argument the
     comment above already makes about the mask stating it a second time, and the
     reason that one is allowed is that a data URI cannot read a custom property. A
     script can.

     `assets/label-warp.js` is a plain deferred script publishing one global, not a
     module, because this file is one too and `defer` orders them by document position.
     `snippets/bottle-stage.liquid` loads it first.

     **If it is missing, nothing here throws.** `warpToCanvas` returns null, `bakeWarp`
     returns before publishing anything, and the cascade in `assets/generator.css`
     falls through to `--sdw-label-img` — the flat picture inside the bowed paper, the
     pre-warp build, the same state a failed fetch or a dead script already produced. */
  const warpToCanvas = (bitmap) => {
    const warp = window.SDW_LABEL_WARP;
    if (!warp) return null;
    return warp.toCanvas(bitmap);
  };

  const warpToBlob = (canvas) => {
    const warp = window.SDW_LABEL_WARP;
    if (!warp) return Promise.resolve(null);
    return warp.toBlob(canvas);
  };

  const bakeWarp = (src) => {
    if (!src) {
      clearWarp();
      return;
    }
    if (src === warpSource && warpUrl) return;
    if (src === warpPending) return;

    warpPending = src;
    const token = ++warpToken;

    (async () => {
      try {
        const bitmap = await bitmapFor(src);
        if (!bitmap || token !== warpToken) return;

        const canvas = warpToCanvas(bitmap);
        if (!canvas || token !== warpToken) return;

        const blob = await warpToBlob(canvas);
        if (!blob || token !== warpToken) return;

        const url = URL.createObjectURL(blob);
        if (token !== warpToken) {
          try {
            URL.revokeObjectURL(url);
          } catch (_) {}
          return;
        }

        revokeWarp();
        warpUrl = url;
        warpSource = src;
        document.documentElement.style.setProperty(WARP_PROP, `url("${url}")`);
      } catch (err) {
        /* The cascade already has an answer for this: the flat picture inside the
           bowed paper, which is what the pre-warp build shipped. A label that is not
           curved beats no label. */
        console.warn('[SDW] label warp bake failed:', err);
      } finally {
        if (warpPending === src) warpPending = '';
      }
    })();
  };

  /* `expand` was the third option here and opened the mobile overlay on the label
     coming back from a generation. The overlay is gone; what that moment does now
     is advance the stepped flow to the results screen, which `assets/generator.js`
     does at the call site because it is the file that knows a round has landed. */
  const setLabel = (url, { settle = false } = {}) => {
    const next = String(url || '');
    const changed = next !== applied.label;
    /* **Read before a single thing below is replaced**: what is on the bottles right
       now, as a URL that will still resolve for the length of a cross-fade (see
       `retireUrl`). `warpUrl` is the baked cylinder, which is what is actually on
       screen; `shownUrl` is the flat picture it was baked from, which is what shows
       in the few milliseconds before the bake lands and after a bake that failed. */
    const outgoing = warpUrl || shownUrl;
    applied.label = next;

    const stage = stageRoot();
    if (stage) stage.toggleAttribute('data-sdw-has-label', !!next);

    /* Named `cssUrl` rather than `url`, because the parameter is already `url` —
       and a `const` shadowing a parameter in the same scope is a syntax error that
       takes the whole file down, `SDW_STAGE` with it.

       `shown` is the short form of the same picture — see `publishableUrl` above.
       Everything that goes into CSS uses it; `applied.label` keeps the original, so
       change detection, the recap and the aspect measurement all still compare the
       thing `generator.js` actually stored. */
    const shown = publishableUrl(next);
    shownUrl = shown;
    const cssUrl = shown ? `url("${shown.replace(/"/g, '%22')}")` : '';

    for (const art of document.querySelectorAll('[data-sdw-art]')) {
      if (next) {
        if (art.style.backgroundImage !== cssUrl) art.style.backgroundImage = cssUrl;
        art.setAttribute('data-sdw-art-filled', '');
      } else {
        art.style.removeProperty('background-image');
        art.removeAttribute('data-sdw-art-filled');
      }
    }

    /* **Two shapes the generated image could be, and one of them is a setting.**
       If it comes back as artwork, it sits in the label's picture window and the
       theme draws the paper, the wordmark and the name around it. If it comes back
       as a finished label — paper, lettering and all — then drawing it *inside*
       another label is a label in a label, so it replaces the band outright and
       the theme's own lettering goes with it.

       **It is a finished label, and that is now the shipped default.** The client
       confirmed it against a real generation: the drawing carries its own paper,
       its own wordmark and its own name, which is why the stage was showing a
       small cropped label inside a larger drawn one. The setting stays, because a
       future prompt change could make it artwork again and that would then be a
       theme-editor edit rather than a code change.

       The bar's own collapsed band takes the branch too — it is a thumbnail of the
       same object. */
    /* **The picture is published once, as a custom property, rather than written
       onto each surface that shows it.** Six bottles, the phone bar's collapsed
       band and the theme's own pre-generation label all want the same picture, and
       one property on the root reaches every one of them in a single write.

       **It must stay a short URL, and that rule outlives the ladder that made it
       urgent.** Until the warp was baked, 240 slice spans inherited this property
       and a `data:` URL here put a copy of the whole base64 string in every one of
       their computed styles — 508ms of blocked main thread on a mid-range CPU at
       the moment the label lands (`D-122`). The spans are gone, but the property is
       still inherited by the whole stage subtree and still re-resolved on every
       invalidation of it, so `publishableUrl` above stays and this line keeps
       getting the short form.

       **`--sdw-label-warped-img` is published beside it, not over it.** That one is
       the baked cylinder and only the bottles read it; this one stays flat, because
       the phone bar's band is a thumbnail of the label the visitor made rather than
       of a bottle. */
    const full = (stage?.getAttribute('data-sdw-label-mode') || 'full') === 'full';
    const root = document.documentElement;
    /* **What wears the label when nobody has drawn one.** `D-113`'s successor
       replaced the plain-type hint with a branded illustration — not a photo, so
       it does not promise a result the visitor has not made yet — baked with the
       same `bakeWarp` a real label gets, so the bottle never shows an unwarped
       picture next to a warped one. Read from the stage root rather than hard-coded,
       because the asset name belongs to the markup, same as every other URL here. */
    const placeholder = full ? stage?.getAttribute('data-sdw-placeholder-label') || '' : '';
    const showingFull = !!next || !!placeholder;
    if (next && full) {
      root.style.setProperty('--sdw-label-img', cssUrl);
      /* Fire-and-forget: the cascade shows the flat picture in the bowed paper
         until this resolves. Not run in the artwork branch, where the theme draws
         the paper and the generated image sits in the picture window — there is no
         label to bend there. */
      bakeWarp(shown);
    } else if (placeholder) {
      root.style.setProperty('--sdw-label-img', `url("${placeholder.replace(/"/g, '%22')}")`);
      bakeWarp(placeholder);
    } else {
      root.style.removeProperty('--sdw-label-img');
      clearWarp();
    }
    for (const label of document.querySelectorAll('[data-sdw-bottle-label], .sdw-flow-nav__band')) {
      label.toggleAttribute('data-sdw-full-label', showingFull && full);
    }
    if (next && full) {
      measureLabelAspect(shown, next);
    } else if (placeholder) {
      /* **Stated, not measured.** `measureLabelAspect` decodes off-screen and
         guards on `applied.label === token`, which the placeholder deliberately
         never touches (see the comment above `showLabel`/`setLabel`'s branches) —
         so that guard would never pass and the property would never land. The
         placeholder's own ratio is fixed and known at build time, so there is
         nothing to decode: `snippets/bottle-stage.liquid` states it directly on
         `data-sdw-placeholder-aspect`, keyed to the shipped asset's own pixels. */
      const aspect = parseFloat(stage?.getAttribute('data-sdw-placeholder-aspect') || '');
      if (aspect > 0) root.style.setProperty('--sdw-label-aspect', String(aspect));
      else clearLabelAspect();
    } else {
      clearLabelAspect();
    }

    /* **The reveal and the comparison, and a label change is one or the other.**
       `settle` is what tells them apart, and `generator.js` sets it on exactly one
       path: the label coming back from a generation. Choosing between labels that
       have already landed asks for no settle and gets the cross-fade instead —
       which is also why neither plays on a re-hydration, a reload or a package
       change, where nothing changed or nothing was there to fade from.

       Only the whole-label branch fades. In the artwork branch the picture is the
       art window's own background and `assets/generator.css` already transitions
       it; there is no second layer there and nothing that needs one. */
    if (next && settle && changed && !reduced()) playSettle();
    else if (next && changed && !settle && !reduced() && full && outgoing && outgoing !== shown) {
      crossfade(outgoing);
    }
    paintRecap();
    announce();
    /* The cart wants a picture of the pack, not of the label. Scheduled rather than
       run: this is the frame the label lands in and nothing here may compete with
       it. */
    buildComposite();
  };

  /*
    The lead settles first and the rest follow a beat behind, which is what makes it
    read as a label landing on a group of bottles rather than as six elements
    animating at once. The delay is written per bottle by the loop rather than by an
    `:nth-child` rule, for the reason `sections/how-it-works.liquid` records at
    `D-99`: a stagger keyed to markup order breaks the moment anything is added to
    the markup around it.

    The class is removed on `animationend` so the animation can be re-armed; a class
    left on would mean the second draw's label never settled.
  */
  /*
    One label being swapped for another, which is the visitor comparing the three
    that came back rather than seeing the first one arrive.

    The outgoing picture is copied onto a layer of its own — `.sdw-bottle__fade`, one
    per bottle, out of flow and invisible at rest — and that layer fades out over the
    incoming label underneath it. So the bottle is never without a whole label on it,
    which is the difference between a cross-fade and a fade: fading the label itself
    would show bare glass through the middle of the swap.

    The token is what stops two swaps in quick succession from cancelling each other.
    Without it the first fade's `animationend` listener is still armed when the second
    fade starts, fires on the second animation's end — both listeners do — and the
    class comes off mid-fade the way it does not come off a fade that runs alone.
  */
  let fadeToken = 0;

  const crossfade = (src) => {
    const css = `url("${src.replace(/"/g, '%22')}")`;
    fadeToken += 1;
    const token = String(fadeToken);
    for (const layer of document.querySelectorAll('[data-sdw-fade]')) {
      layer.style.setProperty('--sdw-fade-img', css);
      layer.dataset.sdwFadeToken = token;
      layer.classList.remove('is-fading');
      /* Force a reflow so removing and re-adding in the same frame restarts it —
         the same line `playSettle` carries below, and the same failure without it:
         a visitor moving quickly between two labels gets no fade on the second. */
      void layer.offsetWidth;
      layer.classList.add('is-fading');
      layer.addEventListener(
        'animationend',
        () => {
          if (layer.dataset.sdwFadeToken !== token) return;
          layer.classList.remove('is-fading');
          layer.style.removeProperty('--sdw-fade-img');
        },
        { once: true }
      );
    }
  };

  const playSettle = () => {
    const labels = Array.from(document.querySelectorAll('[data-sdw-bottle-label]'));
    labels.forEach((label, index) => {
      label.classList.remove('is-settling');
      /* Force a reflow so removing and re-adding in the same frame restarts it. */
      void label.offsetWidth;
      label.style.setProperty('--sdw-settle-delay', `${Math.min(index, 5) * 70}ms`);
      label.classList.add('is-settling');
      label.addEventListener(
        'animationend',
        () => {
          label.classList.remove('is-settling');
          label.style.removeProperty('--sdw-settle-delay');
        },
        { once: true }
      );
    });
  };

  /* ------------------------------------------------------------------ */
  /* the pack, as one picture                                           */
  /* ------------------------------------------------------------------ */
  /*
    **The client's second request, and the honest scope of it.** They asked for "the
    resulting image of the pack to be generated as a single image and then used for
    the checkout product image; some users get confused when they see labels
    different than the ones they created in the cart". The theme's own cart already
    shows the visitor's label (`snippets/cart-products.liquid`) — but it shows the
    **flat** one, a square picture of a label, rather than the pack they spent the
    configurator building. This is what replaces it.

    **What it can and cannot reach, settled rather than assumed.** Shopify's hosted
    checkout draws the *variant* image, and the only mechanism that can override a
    line item's thumbnail is the Cart Transform API's `update` operation, which is
    documented as available to "development stores or stores on a Shopify Plus plan"
    only. This store is not on Plus (`D-48`, `docs/CHECKOUT_AUDIT.md` §5). And the
    property is private, so no Shopify-drawn surface can render it at all. So the
    composite reaches **the cart page and the cart drawer**, which share
    `snippets/cart-products.liquid`, and nothing else. That is most of the value and
    it is where the visitor sees the mismatch.

    **It reads the rendered layout rather than replaying the station table.** The
    obvious build is to read `--sdw-x`, `--sdw-y`, `--sdw-s`, `--sdw-blur` and
    `--sdw-o` out of `assets/generator.css` and re-apply them here — which would put
    that table in two places, and two copies of a table is two answers waiting to
    disagree. Instead every rectangle below is measured off the elements the browser
    has already laid out, so the composite is a photograph of the composition rather
    than a re-derivation of it. A change to any station moves the picture with no
    edit here, and `overflow: clip` on the shell — the rule that crops a wide label
    at the glass — is reproduced by clipping to the shell's own measured box.

    **The bow is the outline only, and that is a measurement rather than a
    shortcut.** The printing's warp is worth about 0.9% of the label's height at the
    top edge. In a 600px composite the lead bottle's label is around 166px tall, so
    the bow is under two pixels there and about a third of a pixel at the 120px the
    cart actually renders. The paper's *outline* is clipped to the same parabola the
    mask uses, because a straight-cornered label reads as a sticker at any size; the
    printing is drawn flat, because at this size warping it would be work nobody can
    see. If the composite is ever wanted at a larger size, this is the line to
    revisit.

    Two things are deliberately not reproduced: the wrap shading and the sheen
    (`.sdw-bottle__label::before` and `::after`). They are lighting, they are worth a
    few per cent of alpha, and at 120px they are invisible.
  */
  const COMPOSITE_SIDE = 600;
  const COMPOSITE_KEY = 'sdw:pack-composite';

  /* The mask's own two quadratics, in the label's box. Same curve as
     `assets/generator.css` — if that path changes, this changes with it. */
  const bowedLabelPath = (ctx, x, y, w, h) => {
    ctx.beginPath();
    ctx.moveTo(x, y + h * 0.009);
    ctx.quadraticCurveTo(x + w / 2, y - h * 0.009, x + w, y + h * 0.009);
    ctx.lineTo(x + w, y + h * 0.9775);
    ctx.quadraticCurveTo(x + w / 2, y + h * 1.0225, x, y + h * 0.9775);
    ctx.closePath();
  };

  /* ---- the Review preview: the same photograph, lit and bent. `D-270` ----
     The note above is right for the cart: at 120px the warp and the lighting are
     invisible. It stopped being right when `D-264` put this picture across the
     whole phone column in Review, 478px tall at 390 x 664 — there the printing
     sits flat on the glass, the wrap shading and the sheen are missing, and a 600px
     square trimmed and stretched to that size is soft. The client noticed the label
     no longer wrapped round the bottle.

     So the same drawing has a second mode, `rich`, used only for that preview: the
     side is sized to the screen it will be shown on, each bottle is drawn from the
     largest render in its `srcset`, the label goes through the stage's own warp
     (`assets/label-warp.js`, the bitmap the stage itself shows), and the two
     lighting layers `.sdw-bottle__label::before` and `::after` are painted over it
     with the same stops as `assets/generator.css`. **If those stops change there,
     they change here.** The cart's `_Pack Image` is the plain 600px file, as before. */
  const largestSrc = (img) => {
    let best = img.currentSrc || img.src;
    let bestW = 0;
    for (const part of (img.getAttribute('srcset') || '').split(',')) {
      const [url, desc] = part.trim().split(/\s+/);
      const w = parseFloat(desc);
      if (url && /w$/.test(desc || '') && w > bestW) {
        bestW = w;
        best = new URL(url, location.href).href;
      }
    }
    return best;
  };

  /* A CSS colour token as `rgba()` at a given alpha, normalised through a canvas so
     a hex, a name or an `rgb()` all work. `--color-foreground-rgb` is a bare
     "r g b" triple and is read as numbers. */
  const tokenRgba = (ctx, name, alpha, fallback) => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    const triple = raw.match(/^(\d+)[\s,]+(\d+)[\s,]+(\d+)$/);
    if (triple) return `rgba(${triple[1]}, ${triple[2]}, ${triple[3]}, ${alpha})`;
    ctx.fillStyle = fallback;
    ctx.fillStyle = raw || fallback;
    const hex = /^#([0-9a-f]{6})$/i.exec(ctx.fillStyle);
    if (!hex) return `rgba(255, 255, 255, ${alpha})`;
    const n = parseInt(hex[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  };

  /* `.sdw-bottle__label::before` (wrap shading across, light from above) and
     `::after` (the per-glass sheen), in that order, over the label's box. The wrap
     shading turns in by `--sdw-shade-in`, read off the label so the full-label 3.5%
     comes with it. */
  const paintLabelLight = (ctx, label, glass, x, y, w, h) => {
    const ink = (a) => tokenRgba(ctx, '--color-foreground-rgb', a, '#000000');
    const white = (a) => tokenRgba(ctx, '--brand-white', a, '#ffffff');
    const fill = (gradient) => {
      ctx.fillStyle = gradient;
      ctx.fillRect(x, y, w, h);
    };
    const across = (stops) => {
      const g = ctx.createLinearGradient(x, 0, x + w, 0);
      for (const [at, colour] of stops) g.addColorStop(Math.max(0, Math.min(1, at)), colour);
      return g;
    };

    const down = ctx.createLinearGradient(0, y, 0, y + h);
    down.addColorStop(0, white(0.16));
    down.addColorStop(0.22, white(0));
    down.addColorStop(0.64, ink(0));
    down.addColorStop(1, ink(0.07));
    fill(down);

    const inset = (parseFloat(getComputedStyle(label).getPropertyValue('--sdw-shade-in')) || 0) / 100;
    const ramp = [[0, 0.46], [0.02, 0.28], [0.05, 0.15], [0.1, 0.075], [0.17, 0.03], [0.28, 0]];
    fill(across([
      [0, ink(0.46)],
      ...ramp.map(([at, a]) => [inset + at, ink(a)]),
      ...ramp.slice().reverse().map(([at, a]) => [1 - inset - at, ink(a)]),
      [1, ink(0.46)],
    ]));

    const flint = glass === 'white' || glass === 'rose';
    const sheen = flint
      ? [[0.06, 0], [0.24, 0.14], [0.44, 0.34], [0.54, 0.38], [0.7, 0.16], [0.88, 0]]
      : [[0.08, 0], [0.22, 0.2], [0.31, 0.34], [0.43, 0.1], [0.54, 0], [0.63, 0.08], [0.72, 0.22], [0.86, 0]];
    fill(across(sheen.map(([at, a]) => [at, white(a)])));
  };

  const blurPxOf = (filter) => {
    const m = /blur\(([\d.]+)px\)/.exec(filter || '');
    return m ? parseFloat(m[1]) : 0;
  };

  const drawPackComposite = async ({ side: outSide = COMPOSITE_SIDE, rich = false } = {}) => {
    const group = document.querySelector('[data-sdw-stage-group]');
    const pack = document.querySelector('[data-sdw-stage-pack][data-sdw-current]');
    if (!group || !pack) return null;

    const gRect = group.getBoundingClientRect();
    if (!gRect.width || !gRect.height) return null;

    const labelSrc = publishableUrl(applied.label);
    const labelBitmap = labelSrc ? await bitmapFor(labelSrc) : null;
    if (!labelBitmap) return null;
    /* The stage's own cylinder, or the flat picture if the warp script is absent —
       the same fallback the stage itself has. */
    const labelPicture = (rich && warpToCanvas(labelBitmap)) || labelBitmap;

    /* **The frame is the bottles' own union box, not the group's.**

       The group is `inline-size: 100%` — as wide as whatever column it is in —
       while the bottles occupy a band in the middle of it. Framing on the group
       therefore made the composite a function of the column's width, and since the
       four-screen rework the stage has two shapes rather than one: a viewport-sized
       fixed box on the screens that hide it, and a short wide box in flow on the
       screens that show it. Measured at 390 x 844, the same three bottles covered
       **37.8%** of the square built from the hidden stage and **7.9%** of the one
       built from the review screen's compact still — the same picture, at a fifth
       of the size, decided by which screen the visitor happened to be on when a
       label or a package last changed.

       The union of the bottles' own boxes is the same rectangle on every screen up
       to scale, so the composite is now determined by the pack and the label and by
       nothing else. Nothing about the composition changes: the bottles keep their
       proportions and their overlaps, and what is dropped is the empty column
       either side of them.

       It falls back to the group's box if the union comes out empty, which is the
       state a pack of zero drawable bottles would leave it in — the loop below
       would draw nothing anyway, and a zero-sized canvas is a worse failure than a
       blank one. */
    const bottleBoxes = Array.from(pack.querySelectorAll('.sdw-bottle__shell'))
      .map((el) => el.getBoundingClientRect())
      .filter((r) => r.width > 0 && r.height > 0);

    let frame = gRect;
    if (bottleBoxes.length) {
      const left = Math.min(...bottleBoxes.map((r) => r.left));
      const top = Math.min(...bottleBoxes.map((r) => r.top));
      const right = Math.max(...bottleBoxes.map((r) => r.right));
      const bottom = Math.max(...bottleBoxes.map((r) => r.bottom));
      if (right > left && bottom > top) frame = { left, top, width: right - left, height: bottom - top };
    }

    /* Square, because the cart renders the thumbnail at a ratio of 1
       (`snippets/cart-products.liquid`). The group is centred in it rather than
       stretched, so the bottles keep their proportions. */
    const side = Math.max(frame.width, frame.height);
    const scale = outSide / side;
    const ox = (side - frame.width) / 2;
    const oy = (side - frame.height) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = outSide;
    canvas.height = outSide;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    if (rich) ctx.imageSmoothingQuality = 'high';

    /* Back to front, so the overlaps land the way the stage draws them. Rank is the
       stage's own depth order and `z-index` is derived from it, so sorting by rank
       descending *is* painting back to front. */
    const bottles = Array.from(pack.querySelectorAll('[data-sdw-bottle]')).sort(
      (a, b) => Number(b.getAttribute('data-sdw-rank') || 0) - Number(a.getAttribute('data-sdw-rank') || 0)
    );

    for (const bottle of bottles) {
      const shell = bottle.querySelector('.sdw-bottle__shell');
      const img = bottle.querySelector('.sdw-bottle__img');
      const label = bottle.querySelector('.sdw-bottle__label');
      if (!shell || !img) continue;

      const bitmap = await bitmapFor(rich ? largestSrc(img) : img.currentSrc || img.src);
      if (!bitmap) continue;

      const cs = getComputedStyle(shell);
      const box = (el) => {
        const r = el.getBoundingClientRect();
        return [(r.left - frame.left + ox) * scale, (r.top - frame.top + oy) * scale, r.width * scale, r.height * scale];
      };

      const [sx, sy, sw, sh] = box(shell);
      const [ix, iy, iw, ih] = box(img);

      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, parseFloat(cs.opacity) || 1));
      const blur = blurPxOf(cs.filter) * scale;
      /* The station's depth-of-field haze, and one soft cast so the group stands on
         something rather than floating. The stage's own two casts are a `drop-shadow`
         on the wrapper above the shell; one is enough at this size. */
      ctx.filter = `${blur ? `blur(${blur.toFixed(2)}px) ` : ''}drop-shadow(0 ${(6 * scale).toFixed(1)}px ${(10 * scale).toFixed(1)}px rgba(40, 20, 20, 0.18))`;

      /* `overflow: clip` on the shell is what crops a label wider than the glass. */
      ctx.beginPath();
      ctx.rect(sx, sy, sw, sh);
      ctx.clip();

      ctx.drawImage(bitmap, ix, iy, iw, ih);

      if (label && label.hasAttribute('data-sdw-full-label')) {
        const [lx, ly, lw, lh] = box(label);
        if (lw > 0 && lh > 0) {
          ctx.save();
          /* The filter is already applied to this whole save-layer; re-declaring it
             here would blur the label a second time. */
          ctx.filter = 'none';
          bowedLabelPath(ctx, lx, ly, lw, lh);
          ctx.clip();
          ctx.drawImage(labelPicture, lx, ly, lw, lh);
          if (rich) paintLabelLight(ctx, label, bottle.getAttribute('data-sdw-glass'), lx, ly, lw, lh);
          ctx.restore();
        }
      }

      ctx.restore();
    }

    return canvas;
  };

  const canvasToFile = (canvas) =>
    new Promise((resolve) => {
      const name = `pack-${Date.now()}`;
      const done = (blob, ext) => {
        if (!blob) return resolve(null);
        try {
          resolve(new File([blob], `${name}.${ext}`, { type: blob.type }));
        } catch (_) {
          resolve(null);
        }
      };
      /* WebP first: measured at 27-36ms against PNG's 118-262ms on a throttled CPU,
         and it carries the alpha the transparent ground needs. Safari has encoded
         WebP since 14; anything older falls through to PNG. */
      try {
        canvas.toBlob((blob) => {
          if (blob && blob.type === 'image/webp') return done(blob, 'webp');
          canvas.toBlob((png) => done(png, 'png'), 'image/png');
        }, 'image/webp', 0.9);
      } catch (_) {
        try {
          canvas.toBlob((png) => done(png, 'png'), 'image/png');
        } catch (__) {
          resolve(null);
        }
      }
    });

  /*
    **Debounced, idle, and never in the way of a tap.** The package switch is a
    same-frame attribute flip by design (`D-50`), so this must not run inside it —
    it is scheduled after, and a second switch before the first composite finishes
    simply replaces it. `applied.label` is re-read at the end because the visitor may
    have chosen a different label while this was drawing.
  */
  /*
    **The press has to be able to wait for this, and that is not a nicety.**
    Measured: `assets/variant-picker.js` settles the form's `id` *later* than this
    composite is built — after a package tap the form still carried the previous
    variant when the composite had already finished. The two converge, because both
    follow the same checked radio, but there is a window of a few hundred
    milliseconds where a visitor who taps a package and presses Add to Cart
    immediately would send the previous package's picture with the new package's
    variant. That is exactly the mismatch this whole feature exists to remove, so
    `assets/generator.js` asks `compositeBusy()` at the press and waits rather than
    shipping a cart line that disagrees with itself.
  */
  let compositeTimer = null;
  let compositeToken = 0;
  let compositePending = null;
  let compositeSettle = null;

  const beginPending = () => {
    if (compositePending) return;
    compositePending = new Promise((resolve) => {
      compositeSettle = resolve;
    });
  };

  const endPending = () => {
    const settle = compositeSettle;
    compositePending = null;
    compositeSettle = null;
    if (settle) settle();
  };

  const compositeBusy = () => !!compositePending;
  const compositeReady = () => compositePending || Promise.resolve();

  /* `D-270`: the Review preview is wanted only where Review shows one — a phone in
     the single-column layout (`D-264`); `assets/generator.js` asks the same
     question before it waits for it. Sized to what that figure can show: 72svh of
     a 900px-high cap, at the device's pixel ratio, between the cart's 600 and the
     1600 the largest bottle render can feed. */
  const PREVIEW_KEY = 'sdw:pack-preview';
  const previewWanted = () =>
    document.documentElement.getAttribute('data-sdw-mobile-layout') === 'single' &&
    !!window.matchMedia?.('(max-width: 749px)')?.matches;
  const previewSide = () =>
    Math.min(1600, Math.max(COMPOSITE_SIDE, Math.round(Math.min(window.innerHeight * 0.72, 900) * (window.devicePixelRatio || 1))));

  const buildComposite = () => {
    if (compositeTimer) clearTimeout(compositeTimer);
    beginPending();
    compositeTimer = setTimeout(async () => {
      compositeTimer = null;
      const token = ++compositeToken;
      const at = applied.label;
      if (!at) {
        document.dispatchEvent(new CustomEvent(COMPOSITE_KEY, { detail: { file: null } }));
        endPending();
        return;
      }
      let published = null;
      try {
        const canvas = await drawPackComposite();
        if (!canvas || token !== compositeToken || applied.label !== at) return;
        const file = await canvasToFile(canvas);
        if (!file || token !== compositeToken || applied.label !== at) return;
        document.dispatchEvent(new CustomEvent(COMPOSITE_KEY, { detail: { file } }));
        published = file;
      } catch (err) {
        /* A composite that cannot be drawn costs the visitor nothing: the cart falls
           back to the flat label, which is what it shows today. */
        console.warn('[SDW] pack composite failed:', err);
      } finally {
        /* Released even on the superseded path, so a press can never wait on a build
           that has already been replaced by a newer one. */
        if (token === compositeToken) endPending();
      }

      /* **After the press is released, never before it.** The preview is a second,
         larger drawing and the cart does not need it, so Add to cart has stopped
         waiting by the time it starts. Tied to the file it illustrates, so the
         generator can tell a late preview from a current one; `blob: null` on a
         failure, so nothing waits out its timeout for a picture that is not coming. */
      if (!published || !previewWanted()) return;
      let blob = null;
      try {
        const rich = await drawPackComposite({ side: previewSide(), rich: true });
        if (token !== compositeToken || applied.label !== at) return;
        if (rich) blob = await new Promise((resolve) => rich.toBlob((b) => resolve(b), 'image/webp', 0.92));
      } catch (err) {
        console.warn('[SDW] pack preview failed:', err);
      }
      if (token !== compositeToken) return;
      document.dispatchEvent(new CustomEvent(PREVIEW_KEY, { detail: { file: published, blob } }));
    }, 260);
  };

  /* ------------------------------------------------------------------ */
  /* after the add                                                      */
  /* ------------------------------------------------------------------ */
  /*
    **The add takes nothing off the screen. `D-143`.**

    What stood here was a deferred reset: the stage was to keep the composition
    while the cart drawer was open and clear when it closed, saying "That one's in
    your cart. Start another?" for fourteen seconds afterwards. It never did.
    `generator.js` triggers the post-add path from a `window.fetch` patch that fires
    on a `setTimeout(…, 0)` the moment the response resolves, while the drawer opens
    later, on `CartAddEvent`, once the theme has parsed that response and rendered
    the cart sections (`assets/cart-drawer.js`). The reset always won: `drawerIsOpen()`
    read false, the stage cleared at once, and the note spent its fourteen seconds
    behind a drawer that had not opened yet. The client's reading of the result was
    that the work had been thrown away — which is what it looked like.

    That path is not repaired here, it is **deleted**, along with the race it
    carried. There is no automatic reset any more: the add marks the stage *landed*
    and changes nothing else. The composition stays, the rail behind it stays, and
    the clearing happens on one press of one button — so there is no drawer to
    observe, no `MutationObserver` on its `open` attribute, and no timer to lose.

    The affirmation is persistent state rather than a transient note, so it also has
    no dismissal handler: it goes when the next bottle is started, and not before.
  */
  /* `.sdw-stage` explicitly, not `stageRoot()`, and it stays that way now that
     nothing writes `data-sdw-stage` on <html> any more. The mobile overlay used to,
     so a bare `[data-sdw-stage]` query returned `<html>` whenever it was open and
     hung the landed attribute on the wrong element. The overlay is deleted and the
     collision cannot recur — but the class is also simply the more exact query for
     the element meant, and loosening it would be a change with nothing to gain. */
  const landedStage = () => document.querySelector('.sdw-stage[data-sdw-stage]');
  const landedBand  = () => document.querySelector('[data-sdw-stage-landed]');
  const landedNote  = () => document.querySelector('[data-sdw-stage-note]');
  const landedAgain = () => document.querySelector('[data-sdw-stage-again]');

  /* Held in module scope so a morph can put the band back: the variant picker
     re-renders the whole `featured-product-information` element from server HTML
     on every package change (`D-50`), which returns the band to its shipped
     `hidden`. A buyer who changes package while landed has not un-bought
     anything. */
  let landedState = null;

  const paintLanded = () => {
    const band  = landedBand();
    const note  = landedNote();
    const again = landedAgain();
    const stage = landedStage();
    if (!band || !note) return;

    if (!landedState) {
      band.hidden = true;
      note.textContent = '';
      if (again) { again.hidden = true; again.textContent = ''; }
      stage?.removeAttribute?.('data-sdw-landed');
      return;
    }

    /* Dismissed with its close button (`D-272`): the card goes and the pack comes
       back to full strength, but the state stays landed — it is still true that the
       bottle is in the cart, and a morph repaints from this same object. */
    if (landedState.dismissed) {
      band.hidden = true;
      stage?.removeAttribute?.('data-sdw-landed');
      return;
    }

    note.textContent = landedState.text || '';
    if (again) {
      again.textContent = landedState.againLabel || '';
      again.hidden = !landedState.againLabel;
    }
    band.hidden = false;
    stage?.setAttribute?.('data-sdw-landed', '');
  };

  const markLanded = ({ text = '', againLabel = '' } = {}) => {
    landedState = { text, againLabel };
    paintLanded();
  };

  const clearLanded = () => {
    landedState = null;
    paintLanded();
  };

  /* Delegated for the same reason as *Create another bottle*'s handler in
     `assets/generator.js`: the card can be re-rendered under a bound listener. */
  document.addEventListener('click', (e) => {
    if (!e.target?.closest?.('[data-sdw-stage-dismiss]') || !landedState) return;
    e.preventDefault();
    landedState = { ...landedState, dismissed: true };
    paintLanded();
  });

  const clearStage = () => {
    setName('');
    /* **`setLabel('')` now sets the aspect itself, so this no longer calls
       `clearLabelAspect()` after it.** A stage cleared for the next bottle that
       kept the last label's aspect would draw the placeholder in somebody else's
       proportions — that risk is still real, but `setLabel`'s own placeholder
       branch already replaces whatever aspect was there with the placeholder's
       known one, and a `clearLabelAspect()` call after it would only erase that
       correct value back to the stylesheet's 0.58 fallback, which is the "taller
       than the actual label" defect this file also fixes. */
    setLabel('');
    /* Back to the waiting ground, not to a style — the next bottle has not been
       drawn yet, and the cleared stage should look like the one a visitor met on
       arrival rather than like one still wearing the last purchase's tint. */
    setStyle('neutral');
  };

  /* Called by `generator.js` when the buyer presses for the next bottle, and by
     nothing else. It un-lands first so the band cannot outlive the composition it
     is about, then clears — with no settle and no reverse of one, because an
     un-building played backwards is a product being taken apart, which is not what
     happened. */
  const reset = () => {
    clearLanded();
    clearStage();
  };

  /* ------------------------------------------------------------------ */
  /* re-hydration                                                       */
  /* ------------------------------------------------------------------ */
  /*
    Called on boot and after every morph. It reads storage rather than trusting the
    in-memory copy, because a second tab, a reload or `generator.js`'s own
    post-add reset can all have moved it — and because reading is cheap and being
    wrong about what is on the bottle is not.
  */
  const rehydrate = () => {
    if (!stageRoot()) return;
    /* The picker preselects an available package after this file boots (`D-38`,
       and `preferAvailablePackage` in `snippets/variant-main-picker.liquid`), so
       the group the server marked current can be the wrong one by the time anyone
       looks. Settling the package first means the name and the label are applied
       to the bottles that are actually on screen. */
    syncPack();
    const saved = readStored();

    /* **The ground follows the chosen label, and falls back through what is
       there.** Since `D-113` each stored image carries its own `style`, so the
       chosen one's is the right answer; the top-level `style` key is the fallback
       for state written by the previous version, which had one style for the whole
       flow; and with neither there is nothing chosen, which is the neutral ground.
       Tolerating both shapes is not optional — a visitor mid-flow when the deploy
       landed has the old one in their browser. */
    let label = '';
    let ground = '';
    if (saved) {
      const images = Array.isArray(saved.generatedImages) ? saved.generatedImages : [];
      const picked =
        (saved.selectedImage && images.find((item) => item && item.url === saved.selectedImage)) ||
        images[0] ||
        null;
      label = picked?.url || '';
      if (label) ground = picked?.style || saved.style || '';
    }

    setStyle(ground || 'neutral');
    setName(typeof saved?.petName === 'string' ? saved.petName : '');
    setLabel(label, { settle: false });

    /* **The one signal outside this file that a label exists**, published here because
       this is the line that has just decided it. `sections/label-cta.liquid` swaps its
       button's wording on it — a visitor looking at their own dog is asked to choose
       their wines rather than to design the labels they can see — and it does so in CSS,
       through `:has()`, so there is no second reader of `localStorage` to disagree with
       this one.

       An attribute rather than an event: the CTA is server-rendered and already on the
       page when this runs, and a boolean the DOM carries survives a repeat of this
       function, a morph and a bfcache restore without anything having to re-announce it.
       Safe against the observer below, which watches `childList` only — an attribute
       write on this element starts no feedback loop (`b677b59`). */
    const root = document.querySelector('[data-sdw-stage-root]');
    if (root) root.toggleAttribute('data-sdw-has-labels', !!label);

    /* **Landed comes back with everything else, from the same bucket.** Two things
       both need it and neither is an event: a reload, where the buyer refreshed or
       came back to the tab and should find the screen they left rather than a blank
       bottle and no explanation; and a package change, where `D-50`'s morph returns
       the band to its shipped `hidden` while the line in the cart is still there.
       Reading it here means one path answers both. `D-143`. */
    const landed = saved?.landed;
    if (landed && typeof landed === 'object' && landed.text) {
      markLanded({ text: landed.text, againLabel: landed.againLabel || '' });
    } else {
      clearLanded();
    }
  };

  /* ------------------------------------------------------------------ */
  /* the mobile overlay, and why there is no longer one                 */
  /* ------------------------------------------------------------------ */
  /*
    **This is where the expand/collapse mechanism used to be, and it is deleted
    rather than left dormant.** The stage was a fixed overlay on a phone, closed at
    rest, opened by tapping the collapsed row in the flow bar or by a generation
    resolving; `openStage` and `closeStage` wrote `data-sdw-stage` on <html> and an
    `aria-expanded` pair on the bar's toggle.

    The four-screen rework deletes both ends of it. There is no collapsed row to tap
    — the bar carries Back and Next and nothing else — and the stage is the top of
    the screen where the wine is chosen rather than a panel over it, so there is
    nothing left to expand into. What went with it: `openStage`, `closeStage`,
    `lastTrigger`, the delegated toggle/close/ground click handlers, the Escape
    handler, the breakpoint-change guard that stopped a fixed panel stranding over a
    desktop layout, and `open`/`close` on `window.SDW_STAGE`.

    A dead mechanism left in place is something the next reader has to disprove, and
    this one would have kept writing an attribute no rule reads.

    **What did not change is the one property the composite depends on.** On the two
    screens that do not show the stage it is still `position: fixed` and still hidden
    by `opacity` and `visibility` rather than by `display` — never `display: none`,
    and never out of layout. `drawPackComposite` below measures this subtree with
    `getBoundingClientRect()`, so a `display: none` stage returns a 0 x 0 group,
    fails that function's own first guard, and ships a cart line with no
    `_Pack Image` on it. `assets/generator.css` carries the measurement.
  */

  /* ------------------------------------------------------------------ */
  /* keeping up with the morph                                          */
  /* ------------------------------------------------------------------ */
  let queued = false;
  const scheduleRehydrate = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      rehydrate();
    });
  };

  let observed = null;
  const observer = new MutationObserver((mutations) => {
    /* Only a change that replaced or added a bottle matters. Anything else in this
       section mutating — a step being hidden, an error appearing — leaves the stage
       exactly as it was, and re-applying state on every one of those would fight
       the visitor's own typing. */
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.('[data-sdw-bottle]') || node.querySelector?.('[data-sdw-bottle]')) {
          scheduleRehydrate();
          return;
        }
      }
    }
  });

  const observe = () => {
    const scope = document.querySelector('[data-sdw-stage-root]')?.closest('.shopify-section');
    if (!scope || scope === observed) return;
    observer.disconnect();
    observer.observe(scope, { childList: true, subtree: true });
    observed = scope;
  };

  document.addEventListener('variant:update', () => {
    scheduleRehydrate();
    observe();
  });

  /* The rail is morphed on every package change and the picker's own script can
     move the checked radio without a `change` event of its own — `checkInput`
     dispatches one, but `syncRadioState` writes `checked` directly on the others.
     A cheap poll of the checked value covers both, and does nothing at all when
     the value has not moved. */
  let lastPackValue = null;
  const watchPack = () => {
    const value = checkedPackInput()?.getAttribute('value') || null;
    if (value === lastPackValue) return;
    lastPackValue = value;
    syncPack();
  };
  document.addEventListener('click', () => window.setTimeout(watchPack, 0), true);
  window.setInterval(watchPack, 500);

  /* ------------------------------------------------------------------ */
  /* the public surface                                                 */
  /* ------------------------------------------------------------------ */
  window.SDW_STAGE = {
    setName,
    setStyle,
    setLabel,
    reset,
    /* `D-143`. The add calls `markLanded`; the press for the next bottle calls
       `reset`, which un-lands on its way through. Nothing else clears either. */
    markLanded,
    clearLanded,
    rehydrate,
    /* For the press. `assets/generator.js` holds the submit until a composite that
       is mid-flight has landed, so a cart line cannot carry one package's picture
       beside another package's variant. */
    compositeBusy,
    compositeReady,
  };

  /* ------------------------------------------------------------------ */
  /* when the work starts                                               */
  /* ------------------------------------------------------------------ */
  /*
    **The first `rehydrate()` waits until the stage is somewhere near the screen,
    because on the home page it is not.**

    `sections/label-cta.liquid` puts a stage on a page where it is the fifth section
    of seventeen, and this file's boot is the most expensive thing on that page: the
    first `rehydrate()` reaches `setLabel`, which reaches `bakeWarp`, which fetches the
    placeholder label, decodes it to an `ImageBitmap`, warps it through a canvas the
    size of the bitmap and encodes the result back out to a blob. Measured on the dev
    server on a load with no scroll at all: the 34 KB label was fetched **twice** —
    once by the CSS that shows it flat and once by the bake — at 661ms and 665ms at
    1280x900, and the baked cylinder landed at 780ms; at 390x844, 585ms, 589ms and
    686ms. All of it for a picture the visitor had not reached and might never reach.

    After this gate the same load fetches neither copy and bakes nothing until the
    stage's own box reaches the viewport. The numbers are in `docs/DECISIONS.md`.

    **`rootMargin` is zero, and that is measured rather than conservative.** A margin
    is the usual way to have the work finished before the thing is looked at, and the
    first version of this used a whole screen — which on this page is a gate that never
    closes. Measured on the dev server: the section's top edge is 27px below the fold
    at 1280x900 and 193px below it at 390x844, because it is the first thing under the
    hero. Any margin at all fires it on load at desktop and most of them do at phone
    width, so the margin was the difference between a deferral and a comment claiming
    one.

    What zero costs is the flash a margin exists to prevent, and the stage's own
    geometry pays it: the label band sits from 39.5% to 81% of the bottle's height
    measured from the top, so the first thing to cross the viewport edge is the feet.
    The bake lands about 250ms after it starts, well inside the scroll that brings the
    band itself up. A bake that has not landed shows the cream band the server
    rendered, which is the same still composition this file's own header says is the
    correct state without it.

    **What is observed is `[data-sdw-stage]` and not `[data-sdw-stage-root]`, and the
    difference is the whole product page.** At 749px and below `assets/generator.css`
    gives the root `display: contents` on every step but 1 and 9 — it is how the stage
    leaves the grid without leaving the markup — and an element with `display: contents`
    generates no box, so an observer on it reports a 0x0 rectangle that never
    intersects anything and the gate would never open. The stage inside it is
    `position: fixed; inset: 0` at that width and in flow above it, so it has a box on
    every screen of both templates.

    **The product page must not be able to lose by this**, and three things together
    are what make that true. The stage there is the media column and intersects on
    load, so the observer fires in the first frame and the gate costs a task. It is
    never `display: none` — `assets/generator.css` hides it with `opacity` and
    `visibility` and says why, which is the same property `drawPackComposite` depends
    on — so it always has a box to intersect with. And a browser without
    `IntersectionObserver` runs `start()` immediately, which is exactly the behaviour
    every browser had before this.

    Two routes still reach `rehydrate()` without the gate, and both are wanted rather
    than tolerated: `variant:update` schedules one, and `window.SDW_STAGE.rehydrate` is
    called by `assets/generator.js`. A stage that is being driven is a stage in use,
    whatever an observer thinks of its rectangle.

    `observe()` is deliberately inside the gate with `rehydrate()`. It attaches the
    mutation observer that answers the package morph, and nothing can morph a section
    the visitor has not reached; `variant:update` calls it on its own besides, so a
    package change that somehow preceded the first intersection is still answered.
  */
  const start = () => {
    rehydrate();
    observe();
  };

  const startWhenNear = () => {
    const stage = stageRoot();
    if (!stage || typeof window.IntersectionObserver !== 'function') {
      start();
      return;
    }

    let fired = false;
    const io = new window.IntersectionObserver(
      (entries) => {
        if (fired || !entries.some((entry) => entry.isIntersecting)) return;
        fired = true;
        io.disconnect();
        start();
      },
      { rootMargin: '0px' }
    );
    io.observe(stage);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startWhenNear);
  else startWhenNear();
})();
